import { Component } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';

/**
 * Error Boundary Component
 * Catches JavaScript errors and displays fallback UI
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    
    // Log error to console
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    // Handle ChunkLoadError specifically - force reload with cache clear
    if (error.name === 'ChunkLoadError' || error.message?.includes('Loading chunk')) {
      console.log('ChunkLoadError detected - clearing cache and reloading');
      // Clear caches
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => caches.delete(name));
        });
      }
      // Clear localStorage version to force refresh
      localStorage.removeItem('app_version');
      // Hard reload
      window.location.reload(true);
      return;
    }
    
    // Save error to localStorage for debugging
    try {
      const errors = JSON.parse(localStorage.getItem('app_errors') || '[]');
      errors.push({
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent
      });
      // Keep only last 10 errors
      localStorage.setItem('app_errors', JSON.stringify(errors.slice(-10)));
    } catch (e) {
      console.warn('Could not save error:', e);
    }

    // Call onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    // Clear caches before reload
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    // Clear version to force refresh
    localStorage.removeItem('app_version');
    // Hard reload
    window.location.reload(true);
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleReset);
      }

      // Default fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <Card className="max-w-lg w-full">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <CardTitle className="text-2xl text-gray-900">
                {this.props.language === 'pt' ? 'Ops! Algo deu errado' : 'Oops! Something went wrong'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600 text-center">
                {this.props.language === 'pt' 
                  ? 'Encontramos um erro inesperado. Por favor, tente novamente.'
                  : 'We encountered an unexpected error. Please try again.'}
              </p>
              
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="bg-gray-100 rounded-lg p-4 text-sm">
                  <div className="flex items-center gap-2 mb-2 font-semibold text-gray-700">
                    <Bug className="w-4 h-4" />
                    Error Details (Dev Only)
                  </div>
                  <pre className="text-xs text-red-600 overflow-auto max-h-32">
                    {this.state.error.toString()}
                  </pre>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button 
                  onClick={this.handleReset}
                  variant="outline"
                  className="flex-1"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {this.props.language === 'pt' ? 'Tentar Novamente' : 'Try Again'}
                </Button>
                <Button 
                  onClick={this.handleGoHome}
                  className="flex-1 bg-[#00C2D1] hover:bg-[#00A8B5]"
                >
                  <Home className="w-4 h-4 mr-2" />
                  {this.props.language === 'pt' ? 'Voltar ao Início' : 'Go Home'}
                </Button>
              </div>

              <p className="text-xs text-gray-400 text-center pt-2">
                {this.props.language === 'pt' 
                  ? 'Se o problema persistir, entre em contato conosco.'
                  : 'If the problem persists, please contact us.'}
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
